"use client";

import { useState, useTransition } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createClient } from "@/lib/supabase/client";
import { consumeAnonClaim } from "@/lib/actions/anon-claim";
import { safeNextPath } from "@/lib/url";
import { GOOGLE_OAUTH_ENABLED } from "@/lib/auth/google-oauth";
import { toast } from "sonner";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(8, "At least 8 characters"),
});

type Values = z.infer<typeof schema>;

export function LoginForm() {
  const router = useRouter();
  const search = useSearchParams();
  const [pending, startTransition] = useTransition();
  const [googleLoading, setGoogleLoading] = useState(false);
  // Constrain to a same-origin path so a crafted ?next=https://evil.com can't
  // turn a successful login into an open redirect.
  const next = safeNextPath(search.get("next"));

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = (values: Values) => {
    startTransition(async () => {
      const supabase = createClient();
      const { error } = await supabase.auth.signInWithPassword(values);
      if (error) {
        toast.error(error.message);
        return;
      }
      // If the user came from the anon publish flow, transfer their draft.
      // The mint sets a non-httpOnly `anon_claim_present=1` marker alongside
      // the real httpOnly token — gate the server-action round-trip on that
      // marker so typical sign-ins skip it entirely. Bounded so a slow/hung
      // claim call can't block the sign-in redirect; if the claim failed the
      // user can retry from the dashboard.
      if (document.cookie.includes("anon_claim_present=1")) {
        try {
          await Promise.race([
            consumeAnonClaim(),
            new Promise<void>((resolve) => setTimeout(resolve, 2500)),
          ]);
        } catch (err) {
          // Server actions strip error details across the boundary; the real
          // message is in the dev terminal under the `[anon-claim]` prefix.
          const msg =
            err instanceof Error ? err.message : "see dev terminal for details";
          console.error("[anon-claim] failed during login:", msg);
        }
      }
      router.push(next);
    });
  };

  const onGoogle = async () => {
    setGoogleLoading(true);
    const supabase = createClient();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`,
      },
    });
    if (error) {
      toast.error(error.message);
      setGoogleLoading(false);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          {...form.register("email")}
        />
        {form.formState.errors.email && (
          <p className="text-sm text-destructive">
            {form.formState.errors.email.message}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">Password</Label>
          <Link
            href="/reset-password"
            className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          {...form.register("password")}
        />
        {form.formState.errors.password && (
          <p className="text-sm text-destructive">
            {form.formState.errors.password.message}
          </p>
        )}
      </div>
      <Button
        type="submit"
        className="w-full rounded-full"
        size="lg"
        disabled={pending}
      >
        {pending ? "Signing in…" : "Sign in"}
      </Button>
      {GOOGLE_OAUTH_ENABLED && (
        <>
          <div className="relative my-2 text-center text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <span className="bg-background px-3">or</span>
            <span className="absolute inset-x-0 top-1/2 -z-10 h-px bg-border" />
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full rounded-full"
            size="lg"
            onClick={onGoogle}
            disabled={googleLoading}
          >
            {googleLoading ? "Redirecting…" : "Continue with Google"}
          </Button>
        </>
      )}
    </form>
  );
}
